import { useForm } from '@deadcow-enterprises/react-forms';
import type { CodeInputProps } from '@/components/code-input/props';
import type { ContactType } from '@/components/contact-selection/props';
import { ErrorSidePanel } from '@/components/errors';
import { useSendCode, useValidateCode } from '@/hooks/api/contact-validation.hooks';
import {
  logger,
  templates,
  useI18n,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import type { HistoryState } from '@tanstack/react-router';
import { useState } from 'react';
import z from 'zod';
import { CodeInputLight } from '@/components/code-input/code-input.light';
import { CodeInputWoo } from '@/components/code-input/code-input.woo';
import { CodeInputWooDark } from '@/components/code-input/code-input.woo-dark';

export const Route = createFileRoute(
  '/(home)/home/(change-password)/_sheet/change-password/code-input/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { state } = useLocation();
  const [initialState] = useState(
    () => (state ?? {}) as { contactType?: ContactType; targetId?: string },
  );
  const { contactType, targetId } = initialState;

  if (!contactType || !targetId) {
    return null;
  }

  return <CodeInputForm contactType={contactType} targetId={targetId} />;
}

function CodeInputForm({
  contactType,
  targetId,
}: Readonly<{ contactType: ContactType; targetId: string }>) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const form = useForm<{ code: string }>(
    {
      code: {
        value: '',
        id: 'contact-validation-otp',
        validator: z
          .string()
          .length(
            widgets.ValidationCodeLength,
            t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString(),
          ),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { sendCode, isSending, captcha } = useSendCode();
  const { validateCode, isValidating } = useValidateCode({
    onSuccess: (securityCode) => {
      void navigate({
        to: '/home/change-password/new-password',
        state: { securityCode } as HistoryState,
      });
    },
    onError: () => {
      form.addError('code', t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString());
      form.setValue('code', '');
    },
  });

  const handleResend = () => {
    form.setValue('code', '');
    if (captcha.challenge && !captcha.token) return;
    sendCode({
      targetId,
      type: contactType,
      resend: true,
      captcha,
    });
  };

  const handleSubmit = ({ code }: typeof form.value) => {
    validateCode({ code, targetId, contactType });
  };

  const handleCodeChange = (value: string) => {
    form.setValue('code', value);
    if (value.length === widgets.ValidationCodeLength) {
      form.updateValidity();
      validateCode({ code: value, targetId, contactType });
    }
  };

  const isProcessing = isValidating;

  const props: CodeInputProps = {
    codeValue: form.value.code,
    codeId: form.ids.code ?? 'contact-validation-otp',
    codeErrors: form.errors.code,
    isValidating: isProcessing,
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
    onCodeChange: handleCodeChange,
    onResend: handleResend,
    onSubmit: form.handleSubmit(handleSubmit),
  };

  return templates.getSkinSelector(skinConfig(props));
}

export function skinConfig(props: CodeInputProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <CodeInputWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <CodeInputWoo {...props} />,
      },
    ],
    defaultValue: <CodeInputLight {...props} />,
  };
}
